import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../services/auth.service';
import axios from 'axios';
import { extractUserAccesses } from '../utils/userAccesses';
import PageLoader from './PageLoader';
import '../styles/ProtectedRoute.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Mapping des routes vers les permissions d'accès requises
const ROUTE_ACCESS_MAP = {
    '/': 'access_dashboard',
    '/nouveautes': 'access_nouveautes',
    '/retours': 'access_retours',
    '/admin/events': 'access_events',
    '/admin/salons': 'access_salons',
    '/admin/materials': 'access_materials',
    '/admin/categories': 'access_categories',
    '/admin/clients': 'access_clients',
    '/admin/parcs': 'access_parcs',
    '/admin/validation-devis': 'access_validation_devis',
    '/admin/preparation': 'access_preparation_logistique',
    '/admin/suivi-retours': 'access_suivi_retours',
    '/admin/users': 'access_users',
    '/admin/entities': 'access_entities',
    '/admin/roles': 'access_roles',
    '/admin/statistiques': 'access_statistiques',
    '/admin/repartition-entites': 'access_repartition',
    '/admin/projets': 'access_projets',
    '/admin/projets-parametres': 'access_projets_parametres',
    '/admin/contacts': 'access_contacts',
    '/admin/devtool': 'access_devtool',
    '/admin/equipe-planning': 'access_equipe_planning',
    '/admin/planning-manager': 'access_planning_manager',
};

// ── Cache module des permissions ─────────────────────────────────────────────
// ProtectedRoute est remonté à CHAQUE changement de route : sans cache, chaque
// navigation était bloquée derrière un GET /users/{id} (donc un écran de
// chargement systématique entre les pages, même pour revenir sur une page déjà
// chargée). On garde les accès en mémoire : la vérification est instantanée,
// et on se resynchronise silencieusement au plus toutes les 60 s.
// La vraie sécurité reste côté API (session + rôles) : ce contrôle-ci n'est
// qu'un confort d'interface.
let permCache = { userId: null, accesses: null, fetchedAt: 0 };
const PERM_REFRESH_MS = 60 * 1000;

const fetchAccesses = async (uid) => {
    const response = await axios.get(`${API_URL}/users/${uid}`, {
        withCredentials: true,
        headers: { 'Accept': 'application/ld+json' },
    });
    const accesses = extractUserAccesses(response.data);
    permCache = { userId: uid, accesses, fetchedAt: Date.now() };
    return accesses;
};

const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const currentUser = authService.getCurrentUser();
    const uid = currentUser?.id || currentUser?.userId || null;

    const cachedForUser = uid !== null && permCache.userId === uid
        ? permCache.accesses
        : null;
    const [accesses, setAccesses] = useState(cachedForUser);
    // Session invalidée côté serveur (401) : on repasse par la case login.
    const [sessionInvalide, setSessionInvalide] = useState(false);

    useEffect(() => {
        if (!uid) return undefined;
        const fresh = permCache.userId === uid
            && permCache.accesses !== null
            && Date.now() - permCache.fetchedAt < PERM_REFRESH_MS;
        if (fresh) return undefined;

        let cancelled = false;
        fetchAccesses(uid)
            .then((list) => { if (!cancelled) setAccesses(list); })
            .catch((err) => {
                if (cancelled) return;
                // 401 : la session serveur n'est plus valide (ou n'a jamais
                // existé malgré un localStorage présent) → déconnexion + login.
                if (err?.response?.status === 401) {
                    authService.logout();
                    setSessionInvalide(true);
                } else if (cachedForUser === null) {
                    // Autre erreur (réseau, 500…) : ne pas bloquer l'interface.
                    setAccesses([]);
                }
            });
        return () => { cancelled = true; };
        // location.pathname : à chaque navigation on vérifie si le cache est
        // périmé (> 60 s) et on se resynchronise en arrière-plan si besoin.
    }, [uid, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pas connecté (ou session invalidée) -> login
    if (!currentUser || sessionInvalide) {
        return <Navigate to="/login" replace />;
    }

    // Loader uniquement au tout premier passage (après login ou F5) :
    // ensuite le cache répond instantanément à chaque navigation.
    if (accesses === null) return <PageLoader />;

    const requiredAccess = ROUTE_ACCESS_MAP[location.pathname];
    const hasAccess = !requiredAccess
        || accesses.length === 0 // aucun accès défini : autoriser (compatibilité)
        || accesses.includes(requiredAccess);

    // Pas d'accès -> message
    if (!hasAccess) {
        return (
            <div className="pr-denied">
                <div className="pr-denied-icon">⛔</div>
                <h1 className="pr-denied-title">Accès refusé</h1>
                <p className="pr-denied-text">
                    Vous n'avez pas les permissions nécessaires pour accéder à cette page.
                    Contactez votre administrateur si vous pensez que c'est une erreur.
                </p>
                <button className="pr-denied-btn" onClick={() => window.location.href = '/'}>
                    Retour à l'accueil
                </button>
            </div>
        );
    }

    // Accès autorisé
    return children;
};

export default ProtectedRoute;
